import React, { useState, useEffect } from 'react';
import { useSplash } from '../../../contexts/SplashContext';
import TranslatableInput from '../../Common/TranslatableInput';
import TranslatableTextarea from '../../Common/TranslatableTextarea';
import LanguageSwitch from '../../Common/LanguageSwitch';
import CommonInput from '../../Common/CommonInput';
import { useHasPermission } from '../../../utils/permissionUtils';
import { fetchSettings } from '../../../store/modules/tailor/tailorActions';
import useFormSettings from '../../../hooks/useFormSettings';


const TailorForm = ({ initialData, onSave, isLoading, onClose }) => {
    const hasCreatePermission = useHasPermission(['create tailors']);
    const hasUpdatePermission = useHasPermission(['update tailors']);

    const { settings, isLoadingSettings, isSettingsInitialized } = useFormSettings('tailor', fetchSettings);
    const splashData = useSplash();
    const languages = splashData?.languages || [];
    const defaultLanguage = languages.find(lang => lang.default)?.code || languages[0]?.code || 'en';
    const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);

    const [formData, setFormData] = useState({
        id: '',
        name: {},
        email: '',
        phone: '',
        password: '',
        employee_code: '',
        is_active: true,
    });
    
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                id: initialData.id || '',
                name: initialData.name || {},
                email: initialData.email || '',
                phone: initialData.phone || '',
                password: initialData.password || '',
                employee_code: initialData.employee_code || '',
                is_active: initialData.is_active,
            });
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSaving(true);
            await onSave(formData);
            setErrors({});
            onClose();
        } catch (error) {
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(key => {
                    newErrors[key] = Array.isArray(error.errors[key]) 
                        ? error.errors[key][0] 
                        : error.errors[key];
                });
                setErrors(newErrors);
            } else {
                setErrors({
                    name: error.message || 'An error occurred while saving'
                });
            }
            return;
        } finally {
            setIsSaving(false);
        }
    };

    // Determine if form is in create or update mode
    const isCreateMode = !formData?.id;
    
    // Check if user has permission for current mode
    const hasPermission = isCreateMode ? hasCreatePermission : hasUpdatePermission;
    
    const isSubmitting = isLoading || isSaving || !hasPermission;
    const selectedLang = languages.find(l => l.code === selectedLanguage);

    if (isLoadingSettings || !isSettingsInitialized) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-end mb-0">

            </div>
            <div className="flex-grow-1">
                <div className="row">
                    <div className="col-md-6">
                        <CommonInput
                            name="employee_code"
                            label="Employee Code"
                            value={formData.employee_code}
                            onChange={handleChange}
                            placeholder="(Auto)"
                            disabled={isSubmitting}
                            required={false}
                            errors={errors}
                        />
                    </div>
                    <div className="col-md-6">
                        <TranslatableInput
                            label="Full Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            errors={errors}
                            required={true}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <CommonInput
                            name="email"
                            label="Email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Enter email"
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                    <div className="col-md-6">
                        <CommonInput
                            name="phone"
                            label="Phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="Enter phone"
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <CommonInput
                            name="password"
                            label="Password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="Enter password"
                            disabled={isSubmitting}
                            errors={errors}
                            required={formData.id ? false : true}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-12">
                        <div className="mb-3">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    name="is_active"
                                    checked={formData.is_active}
                                    onChange={handleChange}
                                    id="is_active"
                                    disabled={isSubmitting}
                                />
                                <label className="form-check-label user-select-none" htmlFor="is_active">
                                    Active
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {hasPermission && (
            <div className="form-footer">
                <button 
                    type="button" 
                    className="btn btn-light" 
                    onClick={onClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </button>
                <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                        </>
                    ) : 'Save'}
                </button>
            </div>
            )}
        </form>
    );
};

export default TailorForm; 